import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import RTSAutoCompleteFilter from "./ShipmentAutoCompleteFilter";
import { DatePicker } from "@mui/x-date-pickers";
import { addDays } from "date-fns";
import {
  SHIPMENT_COLUMN_NAME,
  SHIPMENT_FILTER_COLUMN_OPTIONS,
  SHIPMENT_LABEL_NAME,
} from "./ShipmentConstants";
import { ShipmentResponse } from "../../app/models/responseModels/shipmentResponse";

const COLUMN_WITH_DATE = ["createdOn", "updatedOn"];

type ShipmentDataFilterProps = {
  shipmentData: ShipmentResponse[];
  setFilteredData: React.Dispatch<React.SetStateAction<ShipmentResponse[]>>;
  filteredData: ShipmentResponse[];
};

type FilterOptions = {
  columnName: string;
  inputRank: number | null;
  displayColumnName: string;
};

const ShipmentDataFilter = ({
  shipmentData,
  setFilteredData,
  filteredData,
}: ShipmentDataFilterProps) => {
  // ***** States *****
  const [open, setOpen] = React.useState(false);

  const [inputList, setInputList] = useState<FilterOptions[]>([
    {
      columnName: "",
      inputRank: null,
      displayColumnName: "",
    },
  ]);

  const [clientNameOptions, setClientNameOptions] = useState<string[]>([]); //Dropdown Options
  const [clientNameSelectedOptions, setClientNameSelectedOptions] = useState<
    string[]
  >([]); //Dropdown selected value
  const [externalOrderIDOptions, setExternalOrderIDOptions] = useState<
    string[]
  >([]); //Dropdown Options
  const [externalOrderIDSelectedOptions, setExternalOrderIDSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value
  const [
    externalPartnerShipmentIdOptions,
    setExternalPartnerShipmentIdOptions,
  ] = useState<string[]>([]); //Dropdown Options
  const [
    externalPartnerShipmentIdSelectedOptions,
    setExternalPartnerShipmentIdSelectedOptions,
  ] = useState<string[]>([]); //Dropdown selected value
  const [externalPartnerRegionOptions, setExternalPartnerRegionOptions] =
    useState<string[]>([]); //Dropdown Options
  const [
    externalPartnerRegionSelectedOptions,
    setExternalPartnerRegionSelectedOptions,
  ] = useState<string[]>([]); //Dropdown selected value
  const [recipientNameOptions, setRecipientNameOptions] = useState<string[]>(
    []
  ); //Dropdown Options
  const [recipientNameSelectedOptions, setRecipientNameSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value
  const [recipientCompanyOptions, setRecipientCompanyOptions] = useState<
    string[]
  >([]); //Dropdown Options
  const [recipientCompanySelectedOptions, setRecipientCompanySelectedOptions] =
    useState<string[]>([]); //Dropdown selected value
  const [recipientPhoneOptions, setRecipientPhoneOptions] = useState<string[]>(
    []
  ); //Dropdown Options
  const [recipientPhoneSelectedOptions, setRecipientPhoneSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value
  const [recipientAddressTypeOptions, setRecipientAddressTypeOptions] =
    useState<string[]>([]); //Dropdown Options
  const [
    recipientAddressTypeSelectedOptions,
    setRecipientAddressTypeSelectedOptions,
  ] = useState<string[]>([]); //Dropdown selected value
  const [recipientAddressLine1Options, setRecipientAddressLine1Options] =
    useState<string[]>([]); //Dropdown Options
  const [
    recipientAddressLine1SelectedOptions,
    setRecipientAddressLine1SelectedOptions,
  ] = useState<string[]>([]); //Dropdown selected value
  const [recipientAddressLine2Options, setRecipientAddressLine2Options] =
    useState<string[]>([]); //Dropdown Options
  const [
    recipientAddressLine2SelectedOptions,
    setRecipientAddressLine2SelectedOptions,
  ] = useState<string[]>([]); //Dropdown selected value
  const [recipientCityOptions, setRecipientCityOptions] = useState<string[]>(
    []
  ); //Dropdown Options
  const [recipientCitySelectedOptions, setRecipientCitySelectedOptions] =
    useState<string[]>([]); //Dropdown selected value
  const [recipientStateOptions, setRecipientStateOptions] = useState<string[]>(
    []
  ); //Dropdown Options
  const [recipientStateSelectedOptions, setRecipientStateSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value
  const [recipientZipOptions, setRecipientZipOptions] = useState<string[]>([]); //Dropdown Options
  const [recipientZipSelectedOptions, setRecipientZipSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value
  const [deliveryInstructionsOptions, setDeliveryInstructionsOptions] =
    useState<string[]>([]); //Dropdown Options
  const [
    deliveryInstructionsSelectedOptions,
    setDeliveryInstructionsSelectedOptions,
  ] = useState<string[]>([]); //Dropdown selected value
  const [createdbyOptions, setCreatedbyOptions] = useState<string[]>([]); //Dropdown Options
  const [createdbySelectedOptions, setCreatedbySelectedOptions] = useState<
    string[]
  >([]); //Dropdown selected value
  const [updatedbyOptions, setUpdatedbyOptions] = useState<string[]>([]); //Dropdown Options
  const [updatedbySelectedOptions, setUpdatedbySelectedOptions] = useState<
    string[]
  >([]); //Dropdown selected value

  const [createdOnFromDate, setCreatedOnFromDate] = useState<Date | null>(null);
  const [createdOnToDate, setCreatedOnToDate] = useState<Date | null>(null);

  const [updatedOnFromDate, setUpdatedOnFromDate] = useState<Date | null>(null);
  const [updatedOnToDate, setUpdatedOnToDate] = useState<Date | null>(null);

  const toggle = () => {
    setOpen(!open);
  };

  // ***** Callbacks *****
  const getFilterOptions = useCallback(
    (columnName: string) => {
      if (columnName === SHIPMENT_COLUMN_NAME.CLIENT_NAME) {
        return clientNameOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.EXTERNAL_ORDER_ID) {
        return externalOrderIDOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.EXTERNAL_PARTNER_SHIPMENT_ID) {
        return externalPartnerShipmentIdOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.EXTERNAL_PARTNER_REGION) {
        return externalPartnerRegionOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_NAME) {
        return recipientNameOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_COMPANY) {
        return recipientCompanyOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_PHONE) {
        return recipientPhoneOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_TYPE) {
        return recipientAddressTypeOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_LINE_1) {
        return recipientAddressLine1Options;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_LINE_2) {
        return recipientAddressLine2Options;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_CITY) {
        return recipientCityOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_STATE) {
        return recipientStateOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ZIP) {
        return recipientZipOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.DELIVERY_INSTRUCTIONS) {
        return deliveryInstructionsOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.CREATED_BY) {
        return createdbyOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.UPDATED_BY) {
        return updatedbyOptions;
      }
      return [];
    },
    [
      clientNameOptions,
      externalOrderIDOptions,
      externalPartnerShipmentIdOptions,
      externalPartnerRegionOptions,
      recipientNameOptions,
      recipientCompanyOptions,
      recipientPhoneOptions,
      recipientAddressTypeOptions,
      recipientAddressLine1Options,
      recipientAddressLine2Options,
      recipientCityOptions,
      recipientStateOptions,
      recipientZipOptions,
      deliveryInstructionsOptions,
      createdbyOptions,
      updatedbyOptions,
    ]
  );

  const getSelectedOptions = useCallback(
    (columnName: string) => {
      if (columnName === SHIPMENT_COLUMN_NAME.CLIENT_NAME) {
        return clientNameSelectedOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.EXTERNAL_ORDER_ID) {
        return externalOrderIDSelectedOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.EXTERNAL_PARTNER_SHIPMENT_ID) {
        return externalPartnerShipmentIdSelectedOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.EXTERNAL_PARTNER_REGION) {
        return externalPartnerRegionSelectedOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_NAME) {
        return recipientNameSelectedOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_COMPANY) {
        return recipientCompanySelectedOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_PHONE) {
        return recipientPhoneSelectedOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_TYPE) {
        return recipientAddressTypeSelectedOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_LINE_1) {
        return recipientAddressLine1SelectedOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_LINE_2) {
        return recipientAddressLine2SelectedOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_CITY) {
        return recipientCitySelectedOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_STATE) {
        return recipientStateSelectedOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ZIP) {
        return recipientZipSelectedOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.DELIVERY_INSTRUCTIONS) {
        return deliveryInstructionsSelectedOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.CREATED_BY) {
        return createdbySelectedOptions;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.UPDATED_BY) {
        return updatedbySelectedOptions;
      }
      return [];
    },
    [
      clientNameSelectedOptions,
      externalOrderIDSelectedOptions,
      externalPartnerShipmentIdSelectedOptions,
      externalPartnerRegionSelectedOptions,
      recipientNameSelectedOptions,
      recipientCompanySelectedOptions,
      recipientPhoneSelectedOptions,
      recipientAddressTypeSelectedOptions,
      recipientAddressLine1SelectedOptions,
      recipientAddressLine2SelectedOptions,
      recipientCitySelectedOptions,
      recipientStateSelectedOptions,
      recipientZipSelectedOptions,
      deliveryInstructionsSelectedOptions,
      createdbySelectedOptions,
      updatedbySelectedOptions,
    ]
  );

  const getSelectedFromDate = useCallback(
    (columnName: string) => {
      if (columnName === SHIPMENT_COLUMN_NAME.CREATED_ON) {
        return createdOnFromDate;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.UPDATED_ON) {
        return updatedOnFromDate;
      }
      return null;
    },
    [createdOnFromDate, updatedOnFromDate]
  );

  const getSelectedToDate = useCallback(
    (columnName: string) => {
      if (columnName === SHIPMENT_COLUMN_NAME.CREATED_ON) {
        return createdOnToDate;
      }
      if (columnName === SHIPMENT_COLUMN_NAME.UPDATED_ON) {
        return updatedOnToDate;
      }
      return null;
    },
    [createdOnToDate, updatedOnToDate]
  );

  // ***** UseEffects *****
  useEffect(() => {
    const selectedColumnName = inputList[0].columnName;
    //Client Name dropdown options
    if (selectedColumnName === SHIPMENT_COLUMN_NAME.CLIENT_NAME) {
      setClientNameOptions(
        shipmentData
          .map((v) => v.clientName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setClientNameOptions(
        filteredData
          .map((v) => v.clientName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //External Order ID dropdown options
    if (selectedColumnName === SHIPMENT_COLUMN_NAME.EXTERNAL_ORDER_ID) {
      setExternalOrderIDOptions(
        shipmentData
          .map((v) => v.externalOrderID)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setExternalOrderIDOptions(
        filteredData
          .map((v) => v.externalOrderID)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //External Partner Shipment Id dropdown options
    if (
      selectedColumnName === SHIPMENT_COLUMN_NAME.EXTERNAL_PARTNER_SHIPMENT_ID
    ) {
      setExternalPartnerShipmentIdOptions(
        shipmentData
          .map((v) => v.externalPartnerShipmentId)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setExternalPartnerShipmentIdOptions(
        filteredData
          .map((v) => v.externalPartnerShipmentId)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //External Partner Region Id dropdown options
    if (selectedColumnName === SHIPMENT_COLUMN_NAME.EXTERNAL_PARTNER_REGION) {
      setExternalPartnerRegionOptions(
        shipmentData
          .map((v) => v.externalPartnerRegion)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setExternalPartnerRegionOptions(
        filteredData
          .map((v) => v.externalPartnerRegion)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //Recipient Name dropdown options
    if (selectedColumnName === SHIPMENT_COLUMN_NAME.RECIPIENT_NAME) {
      setRecipientNameOptions(
        shipmentData
          .map((v) => v.recipientName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setRecipientNameOptions(
        filteredData
          .map((v) => v.recipientName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //Recipient Company dropdown options
    if (selectedColumnName === SHIPMENT_COLUMN_NAME.RECIPIENT_COMPANY) {
      setRecipientCompanyOptions(
        shipmentData
          .map((v) => v.recipientCompany)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setRecipientCompanyOptions(
        filteredData
          .map((v) => v.recipientCompany)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //RecipientPhone dropdown options
    if (selectedColumnName === SHIPMENT_COLUMN_NAME.RECIPIENT_PHONE) {
      setRecipientPhoneOptions(
        shipmentData
          .map((v) => v.recipientPhone)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setRecipientPhoneOptions(
        filteredData
          .map((v) => v.recipientPhone)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //Recipient Address Type dropdown options
    if (selectedColumnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_TYPE) {
      setRecipientAddressTypeOptions(
        shipmentData
          .map((v) => v.recipientAddressType)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setRecipientAddressTypeOptions(
        filteredData
          .map((v) => v.recipientAddressType)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //Recipient AddressLine1 dropdown options
    if (selectedColumnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_LINE_1) {
      setRecipientAddressLine1Options(
        shipmentData
          .map((v) => v.recipientAddressLine1)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setRecipientAddressLine1Options(
        filteredData
          .map((v) => v.recipientAddressLine1)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //Recipient AddressLine2 dropdown options
    if (selectedColumnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_LINE_2) {
      setRecipientAddressLine2Options(
        shipmentData
          .map((v) => v.recipientAddressLine2)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setRecipientAddressLine2Options(
        filteredData
          .map((v) => v.recipientAddressLine2)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //Recipient City dropdown options
    if (selectedColumnName === SHIPMENT_COLUMN_NAME.RECIPIENT_CITY) {
      setRecipientCityOptions(
        shipmentData
          .map((v) => v.recipientCity)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setRecipientCityOptions(
        filteredData
          .map((v) => v.recipientCity)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //Recipient State dropdown options
    if (selectedColumnName === SHIPMENT_COLUMN_NAME.RECIPIENT_STATE) {
      setRecipientStateOptions(
        shipmentData
          .map((v) => v.recipientState)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setRecipientStateOptions(
        filteredData
          .map((v) => v.recipientState)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //Recipient ZIP dropdown options
    if (selectedColumnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ZIP) {
      setRecipientZipOptions(
        shipmentData
          .map((v) => v.recipientZip)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setRecipientZipOptions(
        filteredData
          .map((v) => v.recipientZip)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //DeliveryInstructions dropdown options
    if (selectedColumnName === SHIPMENT_COLUMN_NAME.DELIVERY_INSTRUCTIONS) {
      setDeliveryInstructionsOptions(
        shipmentData
          .map((v) => v.deliveryInstructions)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setDeliveryInstructionsOptions(
        filteredData
          .map((v) => v.deliveryInstructions)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //Created By dropdown options
    if (selectedColumnName === SHIPMENT_COLUMN_NAME.CREATED_BY) {
      setCreatedbyOptions(
        shipmentData
          .map((v) => v.createdby)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setCreatedbyOptions(
        filteredData
          .map((v) => v.createdby)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }

    //Updated By dropdown options
    if (selectedColumnName === SHIPMENT_COLUMN_NAME.UPDATED_BY) {
      setUpdatedbyOptions(
        shipmentData
          .map((v) => v.updatedby)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setUpdatedbyOptions(
        filteredData
          .map((v) => v.updatedby)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
  }, [filteredData, inputList, shipmentData]);

  useEffect(() => {
    let newData = [...shipmentData];
    //ClientName Filter
    if (clientNameSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return clientNameSelectedOptions.some((s) => {
          return s === a.clientName;
        });
      });
    }
    //External OrderID Filter
    if (externalOrderIDSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return externalOrderIDSelectedOptions.some((s) => {
          return s === a.externalOrderID;
        });
      });
    }
    //External Partner Shipment Filter
    if (externalPartnerShipmentIdSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return externalPartnerShipmentIdSelectedOptions.some((s) => {
          return s === a.externalPartnerShipmentId;
        });
      });
    }
    //External Partner Region Filter
    if (externalPartnerRegionSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return externalPartnerRegionSelectedOptions.some((s) => {
          return s === a.externalPartnerRegion;
        });
      });
    }
    //Recipient Name Filter
    if (recipientNameSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return recipientNameSelectedOptions.some((s) => {
          return s === a.recipientName;
        });
      });
    }
    //recipient Company Filter
    if (recipientCompanySelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return recipientCompanySelectedOptions.some((s) => {
          return s === a.recipientCompany;
        });
      });
    }
    //Recipient Phone Filter
    if (recipientPhoneSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return recipientPhoneSelectedOptions.some((s) => {
          return s === a.recipientPhone;
        });
      });
    }
    //Recipient Address Type Filter
    if (recipientAddressTypeSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return recipientAddressTypeSelectedOptions.some((s) => {
          return s === a.recipientAddressType;
        });
      });
    }
    //Recipient AddressLine1 Filter
    if (recipientAddressLine1SelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return recipientAddressLine1SelectedOptions.some((s) => {
          return s === a.recipientAddressLine1;
        });
      });
    }
    //Recipient AddressLine2 Filter
    if (recipientAddressLine2SelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return recipientAddressLine2SelectedOptions.some((s) => {
          return s === a.recipientAddressLine2;
        });
      });
    }
    //RecipientCity Filter
    if (recipientCitySelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return recipientCitySelectedOptions.some((s) => {
          return s === a.recipientCity;
        });
      });
    }
    //RecipientState Filter
    if (recipientStateSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return recipientStateSelectedOptions.some((s) => {
          return s === a.recipientState;
        });
      });
    }
    //RecipientZip Filter
    if (recipientZipSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return recipientZipSelectedOptions.some((s) => {
          return s === a.recipientZip;
        });
      });
    }
    //Created By Filter
    if (createdbySelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return createdbySelectedOptions.some((s) => {
          return s === a.createdby;
        });
      });
    }
    //Updated By Filter
    if (updatedbySelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return updatedbySelectedOptions.some((s) => {
          return s === a.updatedby;
        });
      });
    }
    if (createdOnFromDate && createdOnToDate) {
      newData = newData.filter(
        (x) =>
          new Date(x.createdOn).toLocaleDateString() >=
            createdOnFromDate.toLocaleDateString() &&
          new Date(x.createdOn).toLocaleDateString() <=
            createdOnToDate.toLocaleDateString()
      );
    }

    if (updatedOnFromDate && updatedOnToDate) {
      newData = newData.filter(
        (x) =>
          new Date(x.updatedOn).toLocaleDateString() >=
            updatedOnFromDate.toLocaleDateString() &&
          new Date(x.updatedOn).toLocaleDateString() <=
            updatedOnToDate.toLocaleDateString()
      );
    }
    setFilteredData(newData);
  }, [
    shipmentData,
    setFilteredData,
    clientNameSelectedOptions,
    externalOrderIDSelectedOptions,
    externalPartnerRegionSelectedOptions,
    externalPartnerShipmentIdSelectedOptions,
    recipientNameSelectedOptions,
    recipientCompanySelectedOptions,
    recipientPhoneSelectedOptions,
    recipientAddressTypeSelectedOptions,
    recipientAddressLine1SelectedOptions,
    recipientAddressLine2SelectedOptions,
    deliveryInstructionsOptions,
    createdbySelectedOptions,
    updatedbySelectedOptions,
    recipientCitySelectedOptions,
    recipientStateSelectedOptions,
    recipientZipSelectedOptions,
    createdOnFromDate,
    createdOnToDate,
    updatedOnFromDate,
    updatedOnToDate,
  ]);

  // ***** Handlers *****
  const handleListAdd = (index: number) => {
    setInputList([
      ...inputList,
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  const handleRemoveItem = (index: number): void => {
    const newList: Array<FilterOptions> = [...inputList];
    newList.splice(index, 1);
    setInputList(newList);

    const removedColumnName = inputList[index].columnName;
    if (removedColumnName === SHIPMENT_COLUMN_NAME.CLIENT_NAME) {
      return setClientNameSelectedOptions([]);
    }
    if (removedColumnName === SHIPMENT_COLUMN_NAME.EXTERNAL_ORDER_ID) {
      return setExternalOrderIDSelectedOptions([]);
    }
    if (removedColumnName === SHIPMENT_COLUMN_NAME.EXTERNAL_PARTNER_REGION) {
      return setExternalPartnerRegionSelectedOptions([]);
    }
    if (
      removedColumnName === SHIPMENT_COLUMN_NAME.EXTERNAL_PARTNER_SHIPMENT_ID
    ) {
      return setExternalPartnerShipmentIdSelectedOptions([]);
    }
    if (removedColumnName === SHIPMENT_COLUMN_NAME.RECIPIENT_COMPANY) {
      return setRecipientCompanySelectedOptions([]);
    }
    if (removedColumnName === SHIPMENT_COLUMN_NAME.RECIPIENT_NAME) {
      return setRecipientNameSelectedOptions([]);
    }
    if (removedColumnName === SHIPMENT_COLUMN_NAME.RECIPIENT_PHONE) {
      return setRecipientPhoneSelectedOptions([]);
    }
    if (removedColumnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_TYPE) {
      return setRecipientAddressTypeSelectedOptions([]);
    }
    if (removedColumnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_LINE_1) {
      return setRecipientAddressLine1SelectedOptions([]);
    }
    if (removedColumnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_LINE_2) {
      return setRecipientAddressLine2SelectedOptions([]);
    }
    if (removedColumnName === SHIPMENT_COLUMN_NAME.RECIPIENT_CITY) {
      return setRecipientCitySelectedOptions([]);
    }
    if (removedColumnName === SHIPMENT_COLUMN_NAME.RECIPIENT_STATE) {
      return setRecipientStateSelectedOptions([]);
    }
    if (removedColumnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ZIP) {
      return setRecipientZipSelectedOptions([]);
    }
    if (removedColumnName === SHIPMENT_COLUMN_NAME.DELIVERY_INSTRUCTIONS) {
      return setDeliveryInstructionsSelectedOptions([]);
    }
    if (removedColumnName === SHIPMENT_COLUMN_NAME.CREATED_BY) {
      return setCreatedbySelectedOptions([]);
    }
    if (removedColumnName === SHIPMENT_COLUMN_NAME.UPDATED_BY) {
      return setUpdatedbySelectedOptions([]);
    }
  };

  const handleColumnNameChange = (event: any, index: number) => {
    const { value } = event.target;
    const newInputList = [...inputList];
    if (value === SHIPMENT_COLUMN_NAME.CLIENT_NAME) {
      newInputList[index].displayColumnName = SHIPMENT_LABEL_NAME.CLIENT_NAME;
    }
    if (value === SHIPMENT_COLUMN_NAME.EXTERNAL_ORDER_ID) {
      newInputList[index].displayColumnName =
        SHIPMENT_COLUMN_NAME.EXTERNAL_ORDER_ID;
    }
    if (value === SHIPMENT_COLUMN_NAME.EXTERNAL_PARTNER_SHIPMENT_ID) {
      newInputList[index].displayColumnName =
        SHIPMENT_COLUMN_NAME.EXTERNAL_PARTNER_SHIPMENT_ID;
    }
    if (value === SHIPMENT_COLUMN_NAME.EXTERNAL_PARTNER_REGION) {
      newInputList[index].displayColumnName =
        SHIPMENT_COLUMN_NAME.EXTERNAL_PARTNER_REGION;
    }
    if (value === SHIPMENT_COLUMN_NAME.RECIPIENT_NAME) {
      newInputList[index].displayColumnName =
        SHIPMENT_COLUMN_NAME.RECIPIENT_NAME;
    }
    if (value === SHIPMENT_COLUMN_NAME.RECIPIENT_COMPANY) {
      newInputList[index].displayColumnName =
        SHIPMENT_COLUMN_NAME.RECIPIENT_COMPANY;
    }
    if (value === SHIPMENT_COLUMN_NAME.RECIPIENT_PHONE) {
      newInputList[index].displayColumnName =
        SHIPMENT_COLUMN_NAME.RECIPIENT_PHONE;
    }
    if (value === SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_TYPE) {
      newInputList[index].displayColumnName =
        SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_TYPE;
    }
    if (value === SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_LINE_1) {
      newInputList[index].displayColumnName =
        SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_LINE_1;
    }
    if (value === SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_LINE_2) {
      newInputList[index].displayColumnName =
        SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_LINE_2;
    }
    if (value === SHIPMENT_COLUMN_NAME.RECIPIENT_CITY) {
      newInputList[index].displayColumnName =
        SHIPMENT_COLUMN_NAME.RECIPIENT_CITY;
    }
    if (value === SHIPMENT_COLUMN_NAME.RECIPIENT_STATE) {
      newInputList[index].displayColumnName =
        SHIPMENT_COLUMN_NAME.RECIPIENT_STATE;
    }
    if (value === SHIPMENT_COLUMN_NAME.RECIPIENT_ZIP) {
      newInputList[index].displayColumnName =
        SHIPMENT_COLUMN_NAME.RECIPIENT_ZIP;
    }
    if (value === SHIPMENT_COLUMN_NAME.DELIVERY_INSTRUCTIONS) {
      newInputList[index].displayColumnName =
        SHIPMENT_COLUMN_NAME.DELIVERY_INSTRUCTIONS;
    }
    if (value === SHIPMENT_COLUMN_NAME.CREATED_ON) {
      newInputList[index].displayColumnName = SHIPMENT_COLUMN_NAME.CREATED_ON;
    }
    if (value === SHIPMENT_COLUMN_NAME.CREATED_BY) {
      newInputList[index].displayColumnName = SHIPMENT_COLUMN_NAME.CREATED_BY;
    }
    if (value === SHIPMENT_COLUMN_NAME.UPDATED_ON) {
      newInputList[index].displayColumnName = SHIPMENT_COLUMN_NAME.UPDATED_ON;
    }
    if (value === SHIPMENT_COLUMN_NAME.UPDATED_BY) {
      newInputList[index].displayColumnName = SHIPMENT_COLUMN_NAME.UPDATED_BY;
    }
    newInputList[index].columnName = value;
    newInputList[index].inputRank = index;
    setInputList(newInputList);
  };

  const handleReset = (e: any) => {
    setClientNameSelectedOptions([]);
    setExternalOrderIDSelectedOptions([]);
    setExternalPartnerRegionSelectedOptions([]);
    setExternalPartnerShipmentIdSelectedOptions([]);
    setRecipientNameSelectedOptions([]);
    setRecipientCompanySelectedOptions([]);
    setRecipientPhoneSelectedOptions([]);
    setRecipientAddressLine1SelectedOptions([]);
    setRecipientAddressLine2SelectedOptions([]);
    setRecipientAddressTypeSelectedOptions([]);
    setDeliveryInstructionsSelectedOptions([]);
    setCreatedbySelectedOptions([]);
    setUpdatedbySelectedOptions([]);
    setCreatedOnFromDate(null);
    setCreatedOnToDate(null);
    setUpdatedOnFromDate(null);
    setUpdatedOnToDate(null);
    setRecipientCitySelectedOptions([]);
    setRecipientStateSelectedOptions([]);
    setRecipientZipSelectedOptions([]);
    setInputList([
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  const handleSelectedOption = (
    event: Event,
    columnName: string,
    value: string[]
  ) => {
    if (columnName === SHIPMENT_COLUMN_NAME.CLIENT_NAME) {
      return setClientNameSelectedOptions(value);
    }
    if (columnName === SHIPMENT_COLUMN_NAME.EXTERNAL_ORDER_ID) {
      return setExternalOrderIDSelectedOptions(value);
    }
    if (columnName === SHIPMENT_COLUMN_NAME.EXTERNAL_PARTNER_SHIPMENT_ID) {
      return setExternalPartnerShipmentIdSelectedOptions(value);
    }
    if (columnName === SHIPMENT_COLUMN_NAME.EXTERNAL_PARTNER_REGION) {
      return setExternalPartnerRegionSelectedOptions(value);
    }
    if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_NAME) {
      return setRecipientNameSelectedOptions(value);
    }
    if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_COMPANY) {
      return setRecipientCompanySelectedOptions(value);
    }
    if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_PHONE) {
      return setRecipientPhoneSelectedOptions(value);
    }
    if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_TYPE) {
      return setRecipientAddressTypeSelectedOptions(value);
    }
    if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_LINE_1) {
      return setRecipientAddressLine1SelectedOptions(value);
    }
    if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ADDRESS_LINE_2) {
      return setRecipientAddressLine2SelectedOptions(value);
    }
    if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_CITY) {
      return setRecipientCitySelectedOptions(value);
    }
    if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_STATE) {
      return setRecipientStateSelectedOptions(value);
    }
    if (columnName === SHIPMENT_COLUMN_NAME.RECIPIENT_ZIP) {
      return setRecipientZipSelectedOptions(value);
    }
    if (columnName === SHIPMENT_COLUMN_NAME.DELIVERY_INSTRUCTIONS) {
      return setDeliveryInstructionsSelectedOptions(value);
    }
    if (columnName === SHIPMENT_COLUMN_NAME.CREATED_BY) {
      return setCreatedbySelectedOptions(value);
    }
    if (columnName === SHIPMENT_COLUMN_NAME.UPDATED_BY) {
      return setUpdatedbySelectedOptions(value);
    }
  };

  const handleFromDateChange = (value: Date | null, columnName: string) => {
    if (columnName === SHIPMENT_COLUMN_NAME.CREATED_ON) {
      return setCreatedOnFromDate(value);
    }
    if (columnName === SHIPMENT_COLUMN_NAME.UPDATED_ON) {
      return setUpdatedOnFromDate(value);
    }
  };

  const handleToDateChange = (value: Date | null, columnName: string) => {
    if (columnName === SHIPMENT_COLUMN_NAME.CREATED_ON) {
      return setCreatedOnToDate(value);
    }
    if (columnName === SHIPMENT_COLUMN_NAME.UPDATED_ON) {
      return setUpdatedOnToDate(value);
    }
  };

  return (
    <div>
      <Accordion elevation={5} expanded={open}>
        <AccordionSummary
          onClick={toggle}
          expandIcon={
            <>
              <IconButton onClick={toggle} sx={{ color: "white" }}>
                <ExpandMore sx={{ color: "white" }} />
              </IconButton>
            </>
          }
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            minHeight: "36px",
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },

            "&.Mui-expanded": {
              minHeight: "36px",
            },
            backgroundColor: "primary.main",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              color: "white",
            }}
          >
            <Typography>Search Shipment</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ maxHeight: 440, overflowY: "scroll" }}>
          {/* sx={{ height: 220, overflowY: "scroll" }} */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {inputList.map((input, index) => (
              <Grid container rowSpacing={2} columnSpacing={2} key={index}>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  <TextField
                    select
                    key={"columnName"}
                    margin="normal"
                    id={`columnName${index}`}
                    label="Column Name"
                    name={`columnName${index}`}
                    size="small"
                    fullWidth
                    value={inputList[index].columnName}
                    onChange={(event) => handleColumnNameChange(event, index)}
                  >
                    {SHIPMENT_FILTER_COLUMN_OPTIONS.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  {COLUMN_WITH_DATE.includes(inputList[index].columnName) ? (
                    <Grid container rowSpacing={2} columnSpacing={2}>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        {/* <MuiDateRangePicker /> */}
                        <DatePicker
                          minDate={addDays(new Date(), -90)}
                          maxDate={addDays(new Date(), 30)}
                          label={`${inputList[index].displayColumnName} From Date`}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              margin: "normal",
                            },
                            popper: {
                              placement: "auto",
                            },
                          }}
                          onChange={(newValue) =>
                            handleFromDateChange(
                              newValue,
                              inputList[index].columnName
                            )
                          }
                          value={getSelectedFromDate(
                            inputList[index].columnName
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <DatePicker
                          minDate={addDays(new Date(), -90)}
                          maxDate={addDays(new Date(), 30)}
                          label={`${inputList[index].displayColumnName} To Date`}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              margin: "normal",
                            },
                            popper: {
                              placement: "auto",
                            },
                          }}
                          onChange={(newValue) =>
                            handleToDateChange(
                              newValue,
                              inputList[index].columnName
                            )
                          }
                          value={getSelectedToDate(inputList[index].columnName)}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <RTSAutoCompleteFilter
                      columnName={inputList[index].columnName}
                      displayColumnName={inputList[index].displayColumnName}
                      options={getFilterOptions(inputList[index].columnName)}
                      value={getSelectedOptions(inputList[index].columnName)}
                      onChange={(event: any, selectedValue: string[]) => {
                        handleSelectedOption(
                          event,
                          inputList[index].columnName,
                          selectedValue
                        );
                      }}
                    />
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={2}
                  md={2}
                  lg={2}
                  sx={{ alignSelf: "center" }}
                >
                  <CustomTooltip arrow title="Add Filter">
                    <span>
                      <IconButton
                        onClick={() => handleListAdd(index + 1)}
                        disabled={inputList.length === 10}
                      >
                        <AddIcon sx={{ color: "primary.main" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                  <CustomTooltip arrow title="Remove Filter">
                    <span>
                      <IconButton
                        onClick={() => handleRemoveItem(index)}
                        disabled={inputList.length === 1}
                      >
                        <RemoveIcon sx={{ color: "warning.dark" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                </Grid>
              </Grid>
            ))}
          </Box>
          <Grid container spacing={2}>
            <Grid xs={12} item>
              <>
                <Box display={"flex"} justifyContent="end">
                  <Button
                    variant="contained"
                    sx={{ mt: 2 }}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </Box>
              </>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default ShipmentDataFilter;
